import type { Category } from "@/types/search"
import type { AutocompleteCategory } from "@/types/entitySearch"

const MIN_QUERY = 2
const DEBOUNCE_MS = 300

export function debounce<T extends (...args: never[]) => void>(
  fn: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout> | undefined
  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer)
    }
    timer = setTimeout(() => fn(...args), wait)
  }
}

export async function fetchPropertyAutocomplete(
  searchBaseUrl: string,
  value: string
): Promise<Category[]> {
  if (value.length < MIN_QUERY) {
    return []
  }
  const url = `${searchBaseUrl}/api/autocomplete?value=${encodeURIComponent(value)}`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error("Property autocomplete request failed")
  }
  return response.json()
}

async function fetchEntityAutocomplete(
  searchBaseUrl: string,
  endpoint: "agent" | "office",
  value: string
): Promise<AutocompleteCategory[]> {
  if (value.length < MIN_QUERY) {
    return []
  }
  const url = `${searchBaseUrl}/api/autocomplete/${endpoint}?value=${encodeURIComponent(value)}`
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`${endpoint} autocomplete request failed`)
  }
  return response.json()
}

export function fetchAgentAutocomplete(
  searchBaseUrl: string,
  value: string
): Promise<AutocompleteCategory[]> {
  return fetchEntityAutocomplete(searchBaseUrl, "agent", value)
}

export function fetchOfficeAutocomplete(
  searchBaseUrl: string,
  value: string
): Promise<AutocompleteCategory[]> {
  return fetchEntityAutocomplete(searchBaseUrl, "office", value)
}

export { DEBOUNCE_MS, MIN_QUERY }
